import useSWR, { mutate } from 'swr';
import { fetcher, fetcherPost, fetcherPut, fetcherDelete, fetcherWithMethod } from 'utils/axios';
import { handleApiError, showSuccess, showError } from 'utils/errorHandler';
import { CrudEndpoints } from 'types/common';
import { useMemo } from 'react';
import { TaskFilters, UpdateTaskStatusPayload, UpdateDueOnPayload, GenerateUploadFileUrlsPayload, GenerateBlobFileIDPayload, GetFileUrlParams, DeleteTaskFilePayload, GetTaskBoardPayload, getStatusName, getPriorityName, UpdateElapsedMinutesPayload } from 'types/taskManagement/TaskManagement';
import axios, { AxiosRequestConfig } from 'axios';

export const endpoints: CrudEndpoints = {
    key: 'Task',
    getList: '/GetPanelList',
    getListById: '/GetPanelList?TaskID={ID}',
    getModal: '/',
    getLookupList: '/GetLookupList',
    insert: '/',
    update: '/',
    delete: ''
};

export function useGetTaskList(filters: TaskFilters = {}, shouldFetch: boolean = true) {
    const fullEndpoint = `${endpoints.key}${endpoints.getList}`;

    const { data, isLoading, error, isValidating, mutate } = useSWR(
        shouldFetch ? [fullEndpoint, filters] : null,
        ([url, filtersObj]) => fetcherWithMethod('POST', [url, { data: filtersObj, timeout: 0 } as AxiosRequestConfig]),
        {
            revalidateIfStale: true,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            keepPreviousData: true,
            onError: (err) => {
                handleApiError(err);
            }
        }
    );

    return useMemo(
        () => ({
            data: data?.tasks || (Array.isArray(data) ? data : []),
            isLoading,
            error,
            mutate,
            isValidating,
            isEmpty: !isLoading && !(data?.tasks || data)?.length
        }),
        [data, error, isLoading, isValidating]
    );
}

export const useInsertTask = async (task: any) => {
    try {
        const response = await fetcherPost([`${endpoints.key}${endpoints.insert}`, { data: task }]);
        mutateAllTaskQueries();
        return response;
    } catch (error) {
        handleApiError(error);
        throw error;
    }
};

export const useUpdateTask = async (task: any) => {
    try {
        const response = await fetcherPut([`${endpoints.key}${endpoints.update}`, { data: task }]);
        mutateAllTaskQueries();
        return response;
    } catch (error) {
        handleApiError(error);
        throw error;
    }
};

export const refreshTaskInCache = async (taskID: number) => {
    const updatedTask = await fetcher(`Task/${taskID}`);

    await mutate(
        (key: any) => {
            const url =
                typeof key === 'string'
                    ? key
                    : Array.isArray(key)
                        ? key[0]
                        : '';

            return (
                url.includes('Task/GetPanelList') ||
                url.includes('Task/GetList') ||
                url.includes('/TaskBoard')
            );
        },
        (cachedData: any) => {
            if (!cachedData) return cachedData;

            const updateRow = (row: any) => {
                if (row.taskID !== updatedTask.taskID) return row;

                if ('taskStatus' in row || 'taskStatusName' in row) {
                    return {
                        ...row,
                        taskStatus: updatedTask.status,
                        taskStatusName: getStatusName(updatedTask.status),
                        taskPriority: updatedTask.priority,
                        taskPriorityName: getPriorityName(updatedTask.priority),
                        startedOn: updatedTask.startedOn,
                        completedOn: updatedTask.completedOn,
                        updatedOn: updatedTask.updatedOn,
                        updatedByUserName: updatedTask.updatedByUserName
                    };
                }

                return {
                    ...row,
                    status:
                        typeof row.status === 'string'
                            ? getStatusName(updatedTask.status)
                            : updatedTask.status,
                    priority:
                        typeof row.priority === 'string'
                            ? getPriorityName(updatedTask.priority)
                            : updatedTask.priority,
                    startedOn: updatedTask.startedOn,
                    completedOn: updatedTask.completedOn,
                    updatedOn: updatedTask.updatedOn,
                    updatedByUserName: updatedTask.updatedByUserName
                };
            };

            if (Array.isArray(cachedData)) {
                return cachedData.map(updateRow);
            }

            if (Array.isArray(cachedData.tasks)) {
                return {
                    ...cachedData,
                    tasks: cachedData.tasks.map(updateRow)
                };
            }

            return cachedData;
        },
        { revalidate: false }
    );

    await mutate(
        `Task/${taskID}`,
        updatedTask,
        { revalidate: false }
    );

    return updatedTask;
};

export const mutateAllTaskQueries = (extraPath?: string) => {
    mutate((key: any) => {
        if (!key) return false;
        const keyStr = typeof key === 'string' ? key : (Array.isArray(key) ? key[0] : JSON.stringify(key));
        return (
            keyStr.includes('Task/GetPanelList') ||
            keyStr.includes('Task/GetList') ||
            keyStr.includes('/TaskBoard') ||
            (extraPath ? keyStr.includes(extraPath) : false)
        );
    });
};

export const useUpdateTaskStatus = async (
    payload: UpdateTaskStatusPayload,
    path?: string
) => {
    try {
        const response = await fetcherPut([
            `${endpoints.key}/UpdateStatus`,
            { data: payload }
        ]);

        await refreshTaskInCache(payload.taskID);

        showSuccess('Task status updated successfully');
        return response;
    } catch (error) {
        handleApiError(error);
        throw error;
    }
};

export const useUpdateDueOn = async (payload: UpdateDueOnPayload, path?: string) => {
    try {
        const response = await fetcherPut([`${endpoints.key}/UpdateDueOn`, { data: payload }]);
        showSuccess('Task due date updated successfully');
        mutateAllTaskQueries(path);
        return response;
    } catch (error) {
        handleApiError(error);
        throw error;
    }
};

export const useUpdateTaskDueOn = useUpdateDueOn;

export const useUpdateElapsedMinutes = async (payload: UpdateElapsedMinutesPayload, path?: string) => {
    try {
        const response = await fetcherPut([`${endpoints.key}/UpdateElapsedMinutes`, { data: payload }]);
        showSuccess('Elapsed minutes updated successfully');
        mutateAllTaskQueries(path);
        return response;
    } catch (error) {
        handleApiError(error);
        throw error;
    }
};

export const useUpdateTAT = useUpdateElapsedMinutes;


export function useGetTaskModal(taskId?: number | string | null) {
    const endpoint = taskId ? `${endpoints.key}/${taskId}` : null;

    const { data, isLoading, error, isValidating, mutate } = useSWR(
        endpoint,
        fetcher,
        {
            revalidateIfStale: true,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            onError: (err) => {
                handleApiError(err);
            }
        }
    );

    return useMemo(
        () => ({
            data: data || null,
            isLoading,
            error,
            mutate,
            isValidating,
            isEmpty: !isLoading && !data
        }),
        [data, error, isLoading, isValidating, mutate]
    );
}

export const useDeleteTask = async (taskId: number | string) => {
    try {
        const response = await fetcherDelete([`${endpoints.key}/${taskId}`, {}]);
        showSuccess('Task deleted successfully');
        mutateAllTaskQueries();
        return response;
    } catch (error) {
        handleApiError(error);
        throw error;
    }
};

// ==================== Task File Management APIs ====================

// 1. GenerateUploadFileUrls (POST)
export const useGenerateUploadFileUrls = async (payload: GenerateUploadFileUrlsPayload) => {
    try {
        const response = await fetcherPost([`${endpoints.key}/GenerateUploadFileUrls`, { data: payload, timeout: 0 }]);
        return response;
    } catch (error) {
        handleApiError(error);
        throw error;
    }
};

// 2. GenerateBlobFileID (POST)
export const useAfterSuccessfulFileUpload = async (payload: GenerateBlobFileIDPayload) => {
    try {
        const response = await fetcherPost([`${endpoints.key}/GenerateBlobFileID`, { data: payload, timeout: 0 }]);
        return response;
    } catch (error) {
        handleApiError(error);
        throw error;
    }
};

// 3. GetFileUrl (GET) - Fetch download URL for a file
export const useGetFileUrl = async ({ taskID, fileID }: GetFileUrlParams) => {
    try {
        const params: string[] = [];
        if (taskID !== undefined && taskID !== null) params.push(`taskID=${encodeURIComponent(taskID)}`);
        if (fileID && fileID !== 'undefined' && fileID !== 'null' && fileID !== '0') {
            params.push(`fileID=${encodeURIComponent(fileID)}`);
        }
        const queryString = params.length > 0 ? `?${params.join('&')}` : '';

        const response = await fetcher(`${endpoints.key}/GetFileUrl${queryString}`);
        return response;
    } catch (error) {
        handleApiError(error);
        throw error;
    }
};

// 4. GetFiles (GET) - Fetch all files for a task
export const useGetFiles = async (taskID: number | string) => {
    try {
        const response = await fetcher(`${endpoints.key}/GetFiles/${taskID}`);
        return response;
    } catch (error) {
        handleApiError(error);
        throw error;
    }
};

// SWR Hook for GetFiles
export function useGetTaskFiles(taskID?: number | string | null, shouldFetch: boolean = true) {
    const endpoint = (shouldFetch && taskID) ? `${endpoints.key}/GetFiles/${taskID}` : null;

    const { data, isLoading, isValidating, error, mutate } = useSWR(
        endpoint,
        fetcher,
        {
            revalidateIfStale: true,
            revalidateOnFocus: false,
            revalidateOnReconnect: false
        }
    );

    return useMemo(
        () => ({
            data: data || [],
            isLoading,
            isValidating,
            error,
            mutate
        }),
        [data, error, isLoading, isValidating, mutate]
    );
}

export const mutateTaskFiles = (taskID: number | string) => {
    if (!taskID) return;
    const strId = String(taskID);
    mutate((key: any) => {
        if (!key) return false;
        const keyStr = typeof key === 'string' ? key : JSON.stringify(key);
        return (
            keyStr.includes(`Task/GetFiles/${strId}`) ||
            keyStr.includes(`ProjectFileManager/GetList`)
        );
    });
};

// 5. DeleteFile (DELETE)
export const useDeleteTaskFile = async (payload: DeleteTaskFilePayload) => {
    try {
        const response = await fetcherDelete([`${endpoints.key}/DeleteFile`, { data: payload }]);
        showSuccess('File deleted successfully');
        return response;
    } catch (error) {
        handleApiError(error);
        throw error;
    }
};

// ==================== GetTaskBoard API (POST) ====================

// Async POST call for GetTaskBoard
export const useGetTaskBoard = async (payload: GetTaskBoardPayload) => {
    try {
        const response = await fetcherPost([`${endpoints.key}/GetTaskBoard`, { data: payload }]);
        return response;
    } catch (error) {
        handleApiError(error);
        throw error;
    }
};

// Reactive SWR Hook for GetTaskBoard (POST)
export function useGetTaskBoardList(payload: GetTaskBoardPayload, shouldFetch: boolean = true, showAll: boolean = false) {
    const endpoint = `/TaskBoard`;

    const { data, isLoading, error, isValidating, mutate } = useSWR(
        shouldFetch ? [endpoint, payload, showAll] : null,
        ([url, body, showAll]) => {
            return fetcherWithMethod('POST', [url, { data: body, params: { showAll } } as AxiosRequestConfig]);
        },
        {
            revalidateIfStale: true,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            keepPreviousData: true,
            onError: (err) => {
                handleApiError(err);
            }
        }
    );

    return useMemo(
        () => ({
            data: data?.tasks || (Array.isArray(data) ? data : []),
            projects: data?.projects || [],
            contractors: data?.contractors || [],
            totalTaskCount: data?.totalTaskCount || 0,
            dueCount: data?.dueCount ?? 0,
            overdueCount: data?.overdueCount ?? data?.overDueCount ?? 0,
            inProgressCount: data?.inProgressCount ?? 0,
            completedCount: data?.completedCount ?? 0,
            upcomingCount: data?.upcomingCount ?? data?.upComingCount ?? 0,
            pendingCount: data?.pendingCount ?? 0,
            summaryCounts: {
                dueCount: data?.dueCount ?? 0,
                overdueCount: data?.overdueCount ?? data?.overDueCount ?? 0,
                inProgressCount: data?.inProgressCount ?? 0,
                completedCount: data?.completedCount ?? 0,
                upcomingCount: data?.upcomingCount ?? data?.upComingCount ?? 0,
                pendingCount: data?.pendingCount ?? 0,
                totalTaskCount: data?.totalTaskCount ?? 0
            },
            isLoading,
            error,
            mutate,
            isValidating,
            isEmpty: !isLoading && !(data?.tasks || data)?.length
        }),
        [data, error, isLoading, isValidating, mutate]
    );
}




